import type {
  IncidenteResumo,
  SeveridadeIncidente,
  StatusIncidente,
} from '@/data/types';
import { CAPABILITY_MAINTAINER, CAPABILITY_SYSADMIN, temCapability, type MotivoBloqueio } from './capabilities';

/**
 * Regras de incidentes num lugar só — rótulos, ordem, validações de campo
 * (espelho do que a API valida: a API decide, aqui só se avisa antes do clique)
 * e a regra do aviso ao homologar uma versão com incidente aberto.
 */

export const ROTULO_SEVERIDADE: Record<SeveridadeIncidente, string> = {
  baixa: 'Baixa',
  media: 'Média',
  alta: 'Alta',
  critica: 'Crítica',
};

export const ROTULO_STATUS_INCIDENTE: Record<StatusIncidente, string> = {
  aberto: 'Aberto',
  mitigado: 'Mitigado',
  resolvido: 'Resolvido',
};

/** Cor do texto/ponto de cada severidade (o rótulo sempre acompanha: estado nunca é só cor). */
export const COR_SEVERIDADE: Record<SeveridadeIncidente, string> = {
  baixa: 'text-on-surface-muted',
  media: 'text-warning-text',
  alta: 'text-danger',
  critica: 'text-danger',
};

/** Peso para ordenar/comparar: maior = mais grave. */
const PESO_SEVERIDADE: Record<SeveridadeIncidente, number> = { baixa: 0, media: 1, alta: 2, critica: 3 };

export function compararSeveridade(a: SeveridadeIncidente, b: SeveridadeIncidente): number {
  return PESO_SEVERIDADE[a] - PESO_SEVERIDADE[b];
}

export function severidadeMaxima(itens: Array<Pick<IncidenteResumo, 'severidade'>>): SeveridadeIncidente | null {
  let maior: SeveridadeIncidente | null = null;
  for (const item of itens) {
    if (maior === null || compararSeveridade(item.severidade, maior) > 0) maior = item.severidade;
  }
  return maior;
}

/** Aberto e mitigado são os "não resolvidos" — o que conta em `incidentes_abertos`. */
export function naoResolvido(item: Pick<IncidenteResumo, 'status'>): boolean {
  return item.status !== 'resolvido';
}

/** Não resolvidos primeiro (severidade desc, mais novo primeiro), depois os resolvidos — a ordem da API. */
export function ordenarParaVersao<T extends Pick<IncidenteResumo, 'status' | 'severidade' | 'criado_em' | 'id'>>(
  itens: T[],
): T[] {
  return [...itens].sort((a, b) => {
    const resolvidoA = naoResolvido(a) ? 0 : 1;
    const resolvidoB = naoResolvido(b) ? 0 : 1;
    if (resolvidoA !== resolvidoB) return resolvidoA - resolvidoB;
    if (resolvidoA === 0) {
      const porSeveridade = compararSeveridade(b.severidade, a.severidade);
      if (porSeveridade !== 0) return porSeveridade;
    }
    return b.criado_em.localeCompare(a.criado_em) || b.id - a.id;
  });
}

/** Ações de estado oferecidas a partir de cada status. */
export interface AcaoStatusIncidente {
  destino: StatusIncidente;
  rotulo: string;
  principal: boolean;
}

export function acoesDeStatusIncidente(status: StatusIncidente): AcaoStatusIncidente[] {
  switch (status) {
    case 'aberto':
      return [
        { destino: 'mitigado', rotulo: 'Marcar como mitigado', principal: false },
        { destino: 'resolvido', rotulo: 'Resolver', principal: true },
      ];
    case 'mitigado':
      return [
        { destino: 'resolvido', rotulo: 'Resolver', principal: true },
        { destino: 'aberto', rotulo: 'Reabrir', principal: false },
      ];
    default:
      return [{ destino: 'aberto', rotulo: 'Reabrir', principal: false }];
  }
}

/* ------------------------------ Capabilities ------------------------------ */

const LIBERADO: MotivoBloqueio = { permitido: true, motivo: '' };

/** Registrar, editar, mudar estado e (des)associar versões: `maintainer` ou `admin`. */
export function podeEscreverIncidente(roles: string[]): MotivoBloqueio {
  return temCapability(roles, CAPABILITY_MAINTAINER)
    ? LIBERADO
    : { permitido: false, motivo: 'Registrar ou alterar incidentes exige a capability "maintainer" (ou "admin").' };
}

/** Apagar: só `sysadmin` (nem `maintainer` nem `admin` bastam). */
export function podeApagarIncidente(roles: string[]): MotivoBloqueio {
  return roles.includes(CAPABILITY_SYSADMIN)
    ? LIBERADO
    : { permitido: false, motivo: 'Apagar um incidente exige a capability "sysadmin".' };
}

/* -------------------------------- Validação -------------------------------- */

export const TITULO_MIN = 3;
export const TITULO_MAX = 200;

export function validarTitulo(titulo: string): string | null {
  const tamanho = titulo.trim().length;
  if (tamanho < TITULO_MIN) return `O título precisa ter pelo menos ${TITULO_MIN} caracteres.`;
  if (tamanho > TITULO_MAX) return `O título pode ter no máximo ${TITULO_MAX} caracteres.`;
  return null;
}

/** Vazio é válido (vira `null`); preenchido precisa ser URL `http`/`https`. */
export function validarLink(link: string): string | null {
  const valor = link.trim();
  if (valor === '') return null;
  try {
    const url = new URL(valor);
    if (url.protocol === 'http:' || url.protocol === 'https:') return null;
  } catch {
    // cai na mensagem abaixo
  }
  return 'Informe uma URL válida, começando com http:// ou https://.';
}

/** Vazio é válido; preenchido: 2 a 4 segmentos numéricos (`2.2602.80.0`). */
export function validarCorrigidoEm(versao: string): string | null {
  const valor = versao.trim();
  if (valor === '') return null;
  return /^\d+(\.\d+){1,3}$/.test(valor) ? null : 'Use de 2 a 4 números separados por ponto, como 2.2602.80.0.';
}

/* --------------------------- Aviso ao homologar --------------------------- */

export interface AvisoHomologacao {
  /** Incidentes aberto/mitigado que afetam a versão. */
  naoResolvidos: IncidenteResumo[];
  /** Algum deles é alta ou crítica: o aviso fica em destaque. */
  grave: boolean;
  /** Precisa do diálogo "Homologar mesmo assim". Homologar NUNCA é bloqueado: a API deixa passar. */
  exigeConfirmacao: boolean;
}

/**
 * Regra do aviso: homologar uma versão com incidente aberto/mitigado pede
 * confirmação explícita; com alta/crítica o aviso é destacado. Resolvidos não
 * entram. Ordena como a API (mais grave primeiro).
 */
export function avisoDeHomologacao(incidentes: IncidenteResumo[]): AvisoHomologacao {
  const naoResolvidos = ordenarParaVersao(incidentes.filter(naoResolvido));
  return {
    naoResolvidos,
    grave: naoResolvidos.some((item) => item.severidade === 'alta' || item.severidade === 'critica'),
    exigeConfirmacao: naoResolvidos.length > 0,
  };
}
